import React, { useState } from 'react';
import { useIntl } from 'react-intl';

import { Title } from '@xsolla/ui-core';
import { AlertDangerColorful } from '@xsolla/ui-icons';
import { ModalAlert } from '@xsolla/ui-modal';

import { deleteUserSocialProfile } from '../../../../api';
import { useNotifications } from '../../../../hooks/useNotifications';
import { FormFooter } from '../../../ui';

type Props = {
  projectId: string;
  userId: string;
  providerName: string;
  onClose: () => void;
  onRemoved: (providerName: string) => void;
  onFetchingChange: (fetching: boolean) => void;
};

const RemoveSocialProfileModal = ({
  projectId,
  userId,
  providerName,
  onClose,
  onRemoved,
  onFetchingChange
}: Props): JSX.Element => {
  const intl = useIntl();
  const { showErrorNotify, showSuccessNotify } = useNotifications();

  const [fetching, setFetching] = useState(false);
  const [error, setError] = useState(false);

  const onConfirm = async () => {
    setFetching(true);
    onFetchingChange(true);
    setError(false);

    try {
      await deleteUserSocialProfile(projectId, userId, providerName);
      showSuccessNotify();
      onRemoved(providerName);
      onClose();
    } catch (err) {
      setError(true);
      showErrorNotify(err);
    } finally {
      setFetching(false);
      onFetchingChange(false);
    }
  };

  return (
    <ModalAlert
      footer={
        // eslint-disable-next-line react/jsx-wrap-multilines
        <FormFooter
          isModal
          onSubmit={onConfirm}
          onCancel={onClose}
          fetching={fetching}
          error={error}
          submitButtonPalette="danger"
          submitText={intl.formatMessage({
            id: 'users.settings.general-info.social-profile.remove.modal.submit'
          })}
          data-test-id="user-remove-social-profile-modal-footer"
        />
      }>
      <AlertDangerColorful size={40} />
      <Title center indent="sm" data-test-id="user-remove-social-profile-modal-header">
        {intl.formatMessage({
          id: 'users.settings.general-info.social-profile.remove.modal.title'
        })}
      </Title>
      <p>
        {intl.formatMessage(
          { id: 'users.settings.general-info.social-profile.remove.modal.subtitle' },
          { provider: providerName, userId }
        )}
      </p>
    </ModalAlert>
  );
};

export default RemoveSocialProfileModal;
